<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

import Banner from 'dashboard/components-next/banner/Banner.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';

import {
  AGENT_BOT_PERMISSION_GROUPS,
  AGENT_BOT_PERMISSIONS_IN_GROUP_ORDER,
  permissionTranslationKey,
  sanitizeAgentBotPermissions,
} from '../helpers/agentBotPermissions';

const { t } = useI18n();

const modelValue = defineModel({
  type: Array,
  default: () => [],
});

const groups = AGENT_BOT_PERMISSION_GROUPS;

const enabledPermissions = computed(() =>
  sanitizeAgentBotPermissions(modelValue.value)
);

const isEnabled = permission => enabledPermissions.value.includes(permission);

const isEmpty = computed(() => enabledPermissions.value.length === 0);

const counterLabel = computed(() =>
  t('AGENT_BOTS.FORM.PERMISSIONS.COUNT_ENABLED', {
    count: enabledPermissions.value.length,
    total: AGENT_BOT_PERMISSIONS_IN_GROUP_ORDER.length,
  })
);

const groupLabel = (groupId, suffix) =>
  t(`AGENT_BOTS.FORM.PERMISSIONS.GROUPS.${groupId}.${suffix}`);

const permissionLabel = (permission, suffix) =>
  t(
    `AGENT_BOTS.FORM.PERMISSIONS.ITEMS.${permissionTranslationKey(
      permission
    )}.${suffix}`
  );

const togglePermission = (permission, enabled) => {
  const next = new Set(enabledPermissions.value);

  if (enabled) {
    next.add(permission);
  } else {
    next.delete(permission);
  }

  modelValue.value = AGENT_BOT_PERMISSIONS_IN_GROUP_ORDER.filter(value =>
    next.has(value)
  );
};
</script>

<template>
  <fieldset class="flex flex-col gap-3 p-0 m-0 border-0">
    <legend class="p-0 mb-0 contents">
      <div class="flex items-baseline justify-between w-full gap-2">
        <span class="text-sm font-medium text-n-slate-12">
          {{ t('AGENT_BOTS.FORM.PERMISSIONS.LABEL') }}
        </span>
        <span
          class="text-xs text-n-slate-11 tabular-nums"
          data-testid="permissions-counter"
        >
          {{ counterLabel }}
        </span>
      </div>
    </legend>

    <p class="mb-0 text-sm text-n-slate-11">
      {{ t('AGENT_BOTS.FORM.PERMISSIONS.HELP') }}
    </p>

    <Banner v-if="isEmpty" color="slate" data-testid="permissions-empty-banner">
      <div class="flex flex-col gap-1">
        <span class="font-medium text-n-slate-12">
          {{ t('AGENT_BOTS.FORM.PERMISSIONS.EMPTY.TITLE') }}
        </span>
        <span>{{ t('AGENT_BOTS.FORM.PERMISSIONS.EMPTY.BODY') }}</span>
      </div>
    </Banner>

    <!--
      Sem scroll próprio: este bloco vive dentro de um modal que já rola, e dois
      scrolls encaixados prendem a roda do mouse na área interna — a pessoa rola
      e nada se move onde ela está olhando.
      Duas colunas a partir de md porque o modal é `xl`: a largura já existe, e
      usá-la corta a altura da lista pela metade em vez de empilhar 13 itens.
    -->
    <div
      class="grid grid-cols-1 gap-x-6 gap-y-4 md:grid-cols-2"
      data-testid="permissions-groups"
    >
      <div
        v-for="group in groups"
        :key="group.id"
        class="flex flex-col gap-2 break-inside-avoid"
        :data-testid="`permission-group-${group.id}`"
      >
        <div class="flex flex-col">
          <span
            class="text-xs font-semibold tracking-wide uppercase text-n-slate-11"
          >
            {{ groupLabel(group.id, 'LABEL') }}
          </span>
          <span class="text-xs text-n-slate-10">
            {{ groupLabel(group.id, 'HELP') }}
          </span>
        </div>

        <label
          v-for="permission in group.permissions"
          :key="permission.value"
          class="flex items-start gap-3 p-3 border rounded-lg cursor-pointer"
          :class="
            permission.sensitive
              ? 'border-n-amber-5 bg-n-amber-2'
              : 'border-n-weak bg-n-alpha-1 hover:bg-n-alpha-2'
          "
          :data-testid="`permission-${permission.value}`"
        >
          <Checkbox
            class="mt-0.5 flex-shrink-0"
            :model-value="isEnabled(permission.value)"
            @update:model-value="
              value => togglePermission(permission.value, value)
            "
          />
          <span class="flex flex-col min-w-0 gap-1">
            <span class="flex flex-wrap items-center gap-2">
              <span class="text-sm font-medium text-n-slate-12">
                {{ permissionLabel(permission.value, 'LABEL') }}
              </span>
              <span
                v-if="permission.sensitive"
                class="px-1.5 py-0.5 text-xs font-medium rounded text-n-amber-11 bg-n-amber-4"
              >
                {{ t('AGENT_BOTS.FORM.PERMISSIONS.SENSITIVE_LABEL') }}
              </span>
            </span>
            <span class="text-sm text-n-slate-11">
              {{ permissionLabel(permission.value, 'HELP') }}
            </span>
            <span
              v-if="permission.sensitive"
              class="text-sm font-medium text-n-amber-11"
              :data-testid="`permission-warning-${permission.value}`"
            >
              {{ permissionLabel(permission.value, 'WARNING') }}
            </span>
            <code class="text-xs font-mono text-n-slate-10">
              {{ permission.value }}
            </code>
          </span>
        </label>
      </div>
    </div>
  </fieldset>
</template>
